/* eslint-disable jira/filename-case */
import type { ActionThunk, BoundActions, Store, StoreConfig, StoreInstance } from '../types.tsx';
import supports from '../utils/supported-features.tsx';
import { bindActions } from './bind-actions.tsx';
import createStoreState from './create-state.tsx';

export const GLOBAL_SCOPE = '__global__';

interface Registry {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	stores: Map<string, StoreInstance<any, any>>;

	initStore: <TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
		key: string,
		store: Store<TState, TActions>,
		config: StoreConfig,
	) => StoreInstance<TState, TActions>;

	getStore: <TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
		store: Store<TState, TActions>,
		scopeId: string,
		config: StoreConfig | null,
	) => StoreInstance<TState, TActions>;

	hasStore: <TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
		store: Store<TState, TActions>,
		scopeId?: string,
	) => boolean;

	deleteStore: <TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
		store: Store<TState, TActions>,
		scopeId?: string,
	) => void;
}

export class StoreRegistry implements Registry {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	stores: Map<string, StoreInstance<any, any>> = new Map<string, StoreInstance<any, any>>();

	constructor(private defaultScope = '__global__') {
		this.defaultScope = defaultScope;
	}

	initStore = <TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
		key: string,
		Store: Store<TState, TActions>,
		config: StoreConfig,
	): StoreInstance<TState, TActions> => {
		const { initialState, actions, behavior } = Store;

		if (Store.containedBy && !config.contained(Store)) {
			const err = new Error(
				`Store ${Store.key} should be contained by a container but it is used globally. ` +
					'While it might still work, it will likely cause unexpected behaviours.',
			);
			if (supports.scheduling()) Promise.reject(err);
			else throw err;
		}

		const storeState = createStoreState(key, initialState, behavior);
		let boundActions: BoundActions<TState, TActions>;
		const store = {
			storeState,
			// these are used only when container-less, so we generate them on-demand
			get actions() {
				if (!boundActions)
					boundActions = bindActions<TState, TActions>(actions, storeState, config);
				return boundActions;
			},
			behavior,
		};

		this.stores.set(key, store);
		return store;
	};

	hasStore = <TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
		Store: Store<TState, TActions>,
		scopeId: string = this.defaultScope,
	): boolean => {
		const key = this.generateKey(Store, scopeId);
		return this.stores.has(key);
	};

	getStore = <TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
		Store: Store<TState, TActions>,
		scopeId: string = this.defaultScope,
		config: StoreConfig | null = { props: () => ({}), contained: () => false },
	): StoreInstance<TState, TActions> => {
		const key = this.generateKey(Store, scopeId);
		return (
			this.stores.get(key) ||
			// This can actually return null if used outside of react-sweet-state, replicating original source of react-sweet-state so types are the same
			// oxlint-disable-next-line typescript/consistent-type-assertions
			((config && this.initStore(key, Store, config)) as StoreInstance<TState, TActions>)
		);
	};

	deleteStore = <TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
		Store: Store<TState, TActions>,
		scopeId: string = this.defaultScope,
	): void => {
		const key = this.generateKey(Store, scopeId);
		this.stores.delete(key);
	};

	private generateKey = <TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
		Store: Store<TState, TActions>,
		scopeId: string,
	) => `${Store.key}@${scopeId}`;
}

export const defaultRegistry: StoreRegistry = new StoreRegistry();
